// "Diğer ⋯" taşma menüsü (Finar UX P2 Ö2) — riskli/seyrek eylemleri ikincil grubun dışına
// alır. a11y: düğme aria-haspopup/aria-expanded, liste role=menu + menuitem; Esc, Tab ve
// dışarı tıklama kapatır, açılışta odak ilk öğeye, ok/Home/End ile gezinme; seçimden sonra
// odak tetikleyiciye döner (açılan diyalog kapanınca oraya geri gelir).

import { useEffect, useId, useRef, useState, type CSSProperties } from "react";

export interface MoreMenuItem {
  key: string;
  label: string;
  onSelect: () => void;
  /** danger = metin hata rengiyle (geri dönüşü zor eylem). */
  tone?: "default" | "danger";
  disabled?: boolean;
  title?: string;
}

export function MoreMenu({
  label,
  ariaLabel,
  items,
  triggerStyle,
  disabled,
  title,
}: {
  label: string;
  /** Görünür etiket simge taşıyorsa ("Diğer ⋯") erişilebilir ad buradan. */
  ariaLabel?: string;
  items: MoreMenuItem[];
  /** verilirse tetikleyici bu görünümle çizilir (sınıf adı uygulanmaz) */
  triggerStyle?: CSSProperties;
  /** tetikleyici pasif (menü açılmaz) */
  disabled?: boolean;
  title?: string;
}): JSX.Element | null {
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    if (!open) return;
    itemRefs.current.find((b) => b && !b.disabled)?.focus();
    function onDown(e: MouseEvent): void {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent): void {
      if (e.key === "Escape") {
        e.preventDefault();
        setOpen(false);
        buttonRef.current?.focus();
      }
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (items.length === 0) return null;

  function focusEdge(edge: "first" | "last"): void {
    const list = itemRefs.current.filter((b): b is HTMLButtonElement => !!b && !b.disabled);
    (edge === "first" ? list[0] : list[list.length - 1])?.focus();
  }

  function moveFocus(from: number, dir: 1 | -1): void {
    const n = items.length;
    for (let i = 1; i <= n; i += 1) {
      const el = itemRefs.current[(from + dir * i + n) % n];
      if (el && !el.disabled) {
        el.focus();
        return;
      }
    }
  }

  return (
    <div ref={rootRef} style={{ position: "relative" }}>
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="menu"
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
        title={title}
        className={triggerStyle ? undefined : "w-full px-4 py-2 rounded-lg text-xs"}
        style={
          triggerStyle ?? {
            background: "transparent",
            border: "1px solid var(--color-border)",
            color: "var(--color-text-2)",
          }
        }
      >
        {label}
      </button>
      {open && (
        <div
          id={menuId}
          role="menu"
          aria-label={ariaLabel ?? label}
          style={{
            position: "absolute",
            right: 0,
            top: "calc(100% + 4px)",
            minWidth: "100%",
            zIndex: 20,
            background: "var(--color-surface)",
            border: "1px solid var(--color-border)",
            borderRadius: 8,
            boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
            padding: 4,
          }}
        >
          {items.map((it, i) => (
            <button
              key={it.key}
              ref={(el) => {
                itemRefs.current[i] = el;
              }}
              type="button"
              role="menuitem"
              disabled={it.disabled}
              title={it.title}
              onKeyDown={(e) => {
                if (e.key === "ArrowDown") {
                  e.preventDefault();
                  moveFocus(i, 1);
                } else if (e.key === "ArrowUp") {
                  e.preventDefault();
                  moveFocus(i, -1);
                } else if (e.key === "Home") {
                  e.preventDefault();
                  focusEdge("first");
                } else if (e.key === "End") {
                  e.preventDefault();
                  focusEdge("last");
                } else if (e.key === "Tab") {
                  setOpen(false); // odak doğal sırayla ilerler, menü açık kalmaz
                }
              }}
              onClick={() => {
                setOpen(false);
                buttonRef.current?.focus();
                it.onSelect();
              }}
              className="block w-full text-left px-3 py-2 rounded-md text-sm disabled:opacity-50"
              style={{
                background: "transparent",
                border: "none",
                whiteSpace: "nowrap",
                cursor: it.disabled ? "not-allowed" : "pointer",
                color: it.tone === "danger" ? "var(--color-error)" : "var(--color-text)",
              }}
            >
              {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
